import type { RecordSchema } from '@/types'
import { useDebounceFn } from '@vueuse/core'

export const useScrollPaginator = <T = RecordSchema<string | number>>(props: {
	elementId?: string
	filterKey: string
	watch?: Ref<unknown>
	fetchOptions: RecordsFn<T>
}) => {
	const { elementId, fetchOptions, filterKey } = props
	const { toLower } = useUtils()

	// Query
	const optionRecords = ref<T[]>([])
	const paginationFilter = reactive({
		total_count: 0,
		total_pages: 1,
		page: 1,
		current_page: 1,
		loading: false
	})
	const loadingMore = shallowRef(false)
	const isBodyEl = computed(() => toLower(String(elementId)) === 'body')
	const watchOptions = computed(() => props.watch)

	const getOptions = async (payload?: Partial<{ search: string; page: number }>) => {
		if (fetchOptions) {
			try {
				paginationFilter.loading = true
				const result = await fetchOptions({
					...payload,
					page: Number(payload?.page ?? paginationFilter.current_page ?? 1)
				})
				const response = (result as PaginationContext<T[]>) ?? null
				if (response) {
					paginationFilter.total_count = Number(response.total_count ?? 0)
					paginationFilter.total_pages = Number(response.total_pages ?? 0)
					paginationFilter.current_page++
					const list = response.items as T[]
					if (list) {
						// Filter Out to make sure no duplicates is found
						if (!optionRecords.value.length) {
							optionRecords.value = list
						} else {
							const key = filterKey ?? 'id'
							list.forEach((item: T) => {
								if ((item as RecordSchema)?.[key]) {
									// @ts-ignore
									if (optionRecords.value.every(existingItem => existingItem[key] !== item[key])) {
										;(optionRecords.value as RecordSchema[]).push(item as RecordSchema)
									}
								}
							})
						}

						return optionRecords.value
					}
					return []
				}
			} finally {
				paginationFilter.loading = false
			}
		}
	}

	const handleScrollEffect = useDebounceFn(async (e: Event) => {
		if (loadingMore.value || paginationFilter.current_page >= paginationFilter.total_pages + 1) return

		const element = document.querySelector(String(elementId)) as HTMLElement
		let targetEl = (element || e.target) as HTMLElement

		if (element) {
			let scrollTop = element.scrollTop
			let clientHeight = element.clientHeight
			let scrollHeight = element.scrollHeight
			if (isBodyEl.value) {
				scrollTop = document.documentElement.scrollTop || document.body.scrollTop
				clientHeight = document.documentElement.clientHeight || document.body.clientHeight
				scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight
				targetEl = document.documentElement
			}

			// Check if we have scrolled to the end
			const isAtTheEnd = scrollTop + clientHeight >= scrollHeight - 5
			if (isAtTheEnd && paginationFilter.current_page < paginationFilter.total_pages + 1) {
				try {
					loadingMore.value = true
					await getOptions()
					targetEl.scroll({ behavior: 'smooth', top: scrollHeight - paginationFilter.current_page })
				} finally {
					loadingMore.value = false
				}
			}
		}
	}, 100)
	const reset = useDebounceFn(() => {
		optionRecords.value = []
		paginationFilter.current_page = 1
		paginationFilter.total_count = 0
		getOptions()
	}, 200)

	onMounted(() => {
		const element = document.querySelector(String(elementId)) as HTMLElement
		if (toLower(String(elementId)) === 'body') {
			window.addEventListener('scroll', handleScrollEffect)
		} else if (element) {
			element.addEventListener('scroll', handleScrollEffect)
		}
	})
	onUnmounted(() => {
		const element = document.querySelector(String(elementId)) as HTMLElement
		if (toLower(String(elementId)) === 'body') {
			window.removeEventListener('scroll', handleScrollEffect)
		}
		if (element) {
			element.removeEventListener('scroll', handleScrollEffect)
		}
	})

	// Watch for changes and reset the pagination items
	watch(() => props?.watch, reset, { deep: true, immediate: true })

	return { getOptions, paginationFilter, handleScrollEffect, data: optionRecords, watchOptions }
}
